
import { useState, useEffect, useRef } from 'react';
import '../css/DeveloperCenter.css';

function DeveloperCenter({ content }) {
    const [activeSection, setActiveSection] = useState('landing');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [tocItems, setTocItems] = useState([]);
    const contentRef = useRef(null);

    const handleCopy = async (e) => {
        try {
            e.preventDefault();
            e.stopPropagation();
            const container = e.currentTarget.closest('.code-block');
            if (!container) return;
            const contentEl = container.querySelector('.code-content');
            if (!contentEl) return;
            const code = contentEl.textContent || '';

            // Always use textarea fallback for compatibility (http/dev env)
                const ta = document.createElement('textarea');
                ta.value = code;
                ta.setAttribute('readonly', '');
                ta.style.position = 'absolute';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);

            const btn = e.currentTarget;
            const prev = btn.textContent;
            btn.textContent = content?.devCenterCopied || '복사됨!';
            setTimeout(() => { btn.textContent = prev || (content?.devCenterCopy || '복사'); }, 1500);
        } catch (err) {
            // graceful no-op
        }
    };

    const sidebarSections = [
        {
            title: content?.devCenterTitle || '개발자센터',
            items: [
                { id: 'landing', label: content?.devCenterLanding || '랜딩 페이지', active: true }
            ]
        },
        {
            title: content?.devCenterAPIDocs || 'API 문서',
            items: [
                { id: 'map-api', label: content?.devCenterMapAPI || '지도 API' },
                { id: 'spatial-api', label: content?.devCenterSpatialAPI || '공간정보 API' },
                { id: 'routing-api', label: content?.devCenterRoutingAPI || '길찾기 API' },
                { id: 'search-api', label: content?.devCenterSearchAPI || '위치검색 API' }
            ]
        },
        {
            title: content?.devCenterGuide || '가이드',
            items: [
                { id: 'getting-started', label: content?.devCenterGettingStarted || '시작하기 가이드' },
                { id: 'pricing', label: content?.devCenterPricing || '가격 정책' }
            ]
        }
    ];

    // TOC 생성
    useEffect(() => {
        const updateTOC = () => {
            if (!contentRef.current) return;

            const headings = contentRef.current.querySelectorAll('h2, h3');
            const items = [];

            headings.forEach((heading, index) => {
                let id = heading.id;
                const text = heading.textContent.trim();
                const level = heading.tagName.toLowerCase();

                // ID가 없으면 추가
                if (!id) {
                    id = 'heading-' + activeSection + '-' + index;
                    heading.id = id;
                }

                items.push({
                    id: id,
                    text: text,
                    level: level,
                    element: heading
                });
            });

            setTocItems(items);
        };

        // 콘텐츠가 렌더링된 후 TOC 업데이트
        const timeoutId = setTimeout(updateTOC, 150);
        
        return () => clearTimeout(timeoutId);
    }, [activeSection]);

    const scrollToHeading = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const renderMainContent = () => {
        if (activeSection === 'landing') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterLandingTitle || "공간정보 API로 위치 기반 서비스를 쉽게 구축하세요"}</h1>
                    <p className="landing-intro">{content?.devCenterLandingIntro || "SpatialCore API를 사용하여 강력한 위치 기반 애플리케이션을 빠르게 개발할 수 있습니다."}<br/>
                    {content?.devCenterLandingIntro2 || "별도 설치 없이 API 키만으로 즉시 사용 가능한 완전관리형(Managed) SaaS 서비스입니다."}</p>
                    
                    <div className="feature-grid">
                        <div className="feature-card">
                            <h2>{content?.devCenterFeatureMap || "지도 렌더링 API"}</h2>
                            <p>{content?.devCenterFeatureMapDesc || "고성능 지도 표시 및 다양한 커스터마이징 옵션을 제공합니다. 마커, 폴리라인, 폴리곤 등 다양한 오버레이를 지원하며, 이벤트 핸들링을 통한 인터랙티브한 지도 구현이 가능합니다."}</p>
                        </div>
                        <div className="feature-card">
                            <h2>{content?.devCenterFeatureBoundary || "행정경계 데이터 API"}</h2>
                            <p>{content?.devCenterFeatureBoundaryDesc || "전국 행정구역 경계 데이터를 실시간으로 조회할 수 있습니다. 시도, 시군구, 읍면동 단위로 조회가 가능하며, GeoJSON 형식으로 제공되어 다양한 GIS 도구와 호환됩니다."}</p>
                        </div>
                        <div className="feature-card">
                            <h2>{content?.devCenterFeatureRouting || "길찾기/경로 탐색 API"}</h2>
                            <p>{content?.devCenterFeatureRoutingDesc || "자동차, 대중교통, 도보 경로 탐색과 실시간 교통정보를 제공합니다. 다중 경유지 지원 및 경로 최적화 기능으로 효율적인 경로 계획을 수립할 수 있습니다."}</p>
                        </div>
                        <div className="feature-card">
                            <h2>{content?.devCenterFeatureSearch || "위치검색/지오코딩 API"}</h2>
                            <p>{content?.devCenterFeatureSearchDesc || "주소 검색, 좌표 변환, POI 검색 등 다양한 위치 검색 기능을 지원합니다. 실시간 자동완성 기능으로 사용자 경험을 향상시킬 수 있습니다."}</p>
                        </div>
                    </div>

                    <h2>{content?.devCenterFeaturesTitle || "주요 특징"}</h2>
                    <ul>
                        <li><strong>{content?.devCenterFeature1 || "빠른 응답 속도"}</strong>: {content?.devCenterFeature1Desc || "고성능 서버 인프라로 빠른 API 응답 시간을 보장합니다."}</li>
                        <li><strong>{content?.devCenterFeature2 || "확장 가능한 아키텍처"}</strong>: {content?.devCenterFeature2Desc || "대용량 트래픽에도 안정적인 서비스를 제공합니다."}</li>
                        <li><strong>{content?.devCenterFeature3 || "상세한 문서"}</strong>: {content?.devCenterFeature3Desc || "완벽한 API 문서와 코드 예제를 제공합니다."}</li>
                        <li><strong>{content?.devCenterFeature4 || "다양한 SDK 지원"}</strong>: {content?.devCenterFeature4Desc || "JavaScript, Python, Java 등 다양한 언어의 SDK를 제공합니다."}</li>
                        <li><strong>{content?.devCenterFeature5 || "실시간 지원"}</strong>: {content?.devCenterFeature5Desc || "기술 지원팀이 실시간으로 도움을 드립니다."}</li>
                    </ul>

                    <div className="cta-section">
                        <a href="#getting-started" className="cta-button primary" onClick={(e) => {
                            e.preventDefault();
                            setActiveSection('getting-started');
                        }}>{content?.devCenterQuickStart || "빠른 시작 가이드"}</a>
                    </div>
                </div>
            );
        }

        if (activeSection === 'map-api') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterMapAPITitle || "지도 API"}</h1>
                    <p>{content?.devCenterMapAPIDesc || "지도를 표시하고 마커, 오버레이를 추가하며 커스터마이징할 수 있는 강력한 지도 API입니다."}</p>
                    
                    <h2>{content?.devCenterMapDisplayControl || "지도 표시 및 컨트롤"}</h2>
                    <p>{content?.devCenterMapDisplayControlDesc || "기본 지도를 표시하고 확대/축소, 이동 등의 컨트롤을 추가할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={handleCopy}>{content?.devCenterCopy || "복사"}</button>
                        </div>
                        <div className="code-content">{`import { SpatialMap } from '@spatialcore/maps';

const map = new SpatialMap({
  apiKey: 'YOUR_API_KEY',
  container: 'map',
  center: [126.9780, 37.5665], // [경도, 위도]
  zoom: 12,
  controls: {
    zoom: true,
    scale: true,
    fullscreen: true,
    rotation: false
  }
});`}</div>
                    </div>

                    <h3>{content?.devCenterKeyParams || "주요 파라미터"}</h3>
                    <ul>
                        <li><code>apiKey</code>: {content?.devCenterAPIKeyParam || "발급받은 API 키"}</li>
                        <li><code>container</code>: {content?.devCenterContainerParam || "지도를 표시할 HTML 엘리먼트 ID"}</li>
                        <li><code>center</code>: {content?.devCenterCenterParam || "초기 중심 좌표 [경도, 위도]"}</li>
                        <li><code>zoom</code>: {content?.devCenterZoomParam || "초기 줌 레벨 (1-18)"}</li>
                        <li><code>controls</code>: {content?.devCenterControlsParam || "표시할 컨트롤 옵션"}</li>
                    </ul>
                    
                    <h2>{content?.devCenterOverlayAdd || "마커/오버레이 추가"}</h2>
                    <p>{content?.devCenterOverlayAddDesc || "지도 위에 마커, 폴리라인, 폴리곤 등의 오버레이를 쉽게 추가할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={handleCopy}>{content?.devCenterCopy || "복사"}</button>
                        </div>
                        <div className="code-content">{`// 마커 추가
const marker = map.addMarker({
  position: [126.9780, 37.5665],
  title: '서울시청',
  icon: 'custom-icon.png',
  clickable: true
});

// 폴리라인 추가
const polyline = map.addPolyline({
  path: [
    [126.9780, 37.5665],
    [127.0286, 37.4979]
  ],
  strokeColor: '#FF0000',
  strokeWeight: 3
});

// 폴리곤 추가
const polygon = map.addPolygon({
  path: [
    [126.9780, 37.5665],
    [127.0286, 37.5665],
    [127.0286, 37.4979],
    [126.9780, 37.4979]
  ],
  fillColor: '#0000FF',
  fillOpacity: 0.3,
  strokeColor: '#0000FF',
  strokeWeight: 2
});

// 마커 이벤트
marker.on('click', () => {
  console.log('마커가 클릭되었습니다');
});`}</div>
                    </div>
                    
                    <h2>{content?.devCenterStyleCustom || "지도 스타일 커스터마이징"}</h2>
                    <p>{content?.devCenterStyleCustomDesc || "다양한 지도 스타일 테마를 제공하며, 커스텀 스타일도 적용할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={handleCopy}>{content?.devCenterCopy || "복사"}</button>
                        </div>
                        <div className="code-content">{`// 기본 스타일 테마 적용
map.setStyle('dark'); // 'light', 'dark', 'satellite', 'terrain'

// 커스텀 스타일 적용
map.setStyle({
  backgroundColor: '#000000',
  roads: {
    color: '#FFFFFF',
    width: 2
  },
  labels: {
    color: '#CCCCCC',
    fontSize: 12
  }
});

// 스타일 JSON 파일 로드
map.loadStyle('path/to/custom-style.json');`}</div>
                    </div>
                    
                    <h2>{content?.devCenterEventHandling || "이벤트 핸들링"}</h2>
                    <p>{content?.devCenterEventHandlingDesc || "클릭, 드래그, 줌 등 다양한 사용자 이벤트를 처리할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={handleCopy}>{content?.devCenterCopy || "복사"}</button>
                        </div>
                        <div className="code-content">{`// 지도 클릭 이벤트
map.on('click', (event) => {
  console.log('클릭 위치:', event.latlng);
  // 클릭한 위치에 마커 추가
  map.addMarker({
    position: event.latlng,
    title: '클릭한 위치'
  });
});

// 지도 이동 이벤트
map.on('move', () => {
  const center = map.getCenter();
  console.log('현재 중심 좌표:', center);
});

// 줌 변경 이벤트
map.on('zoom', (event) => {
  console.log('현재 줌 레벨:', event.zoom);
});

// 드래그 시작/종료 이벤트
map.on('dragstart', () => {
  console.log('드래그 시작');
});

map.on('dragend', () => {
  console.log('드래그 종료');
});`}</div>
                    </div>
                </div>
            );
        }

        if (activeSection === 'spatial-api') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterSpatialAPITitle || "공간정보 API"}</h1>
                    <p>{content?.devCenterSpatialAPIDesc || "행정구역 경계 데이터와 지역별 통계 정보를 제공하는 공간정보 API입니다."}</p>
                    
                    <h2>{content?.devCenterBoundaryQuery || "행정구역 경계 데이터 조회"}</h2>
                    <p>{content?.devCenterBoundaryQueryDesc || "시도, 시군구, 읍면동 등 각 행정구역의 경계 데이터를 조회할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={handleCopy}>{content?.devCenterCopy || "복사"}</button>
                        </div>
                        <div className="code-content">{`import { SpatialAPI } from '@spatialcore/maps';

const api = new SpatialAPI('YOUR_API_KEY');

// 시도 경계 데이터 조회
const sidoBoundary = await api.getBoundary({
  level: 'sido', // 'sido', 'sigungu', 'eupmyeondong'
  code: '11' // 서울특별시
});

// 시군구 경계 데이터 조회
const sigunguBoundary = await api.getBoundary({
  level: 'sigungu',
  code: '1100' // 서울특별시 강남구
});

// 좌표 기준 행정구역 조회
const adminArea = await api.getAdminAreaByCoord({
  lat: 37.5665,
  lng: 126.9780
});

console.log(adminArea);
// {
//   sido: '서울특별시',
//   sigungu: '중구',
//   eupmyeondong: '을지로동'
// }`}</div>
                    </div>

                    <h3>{content?.devCenterAPIEndpoint || "API 엔드포인트"}</h3>
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>HTTP</span>
                            <button className="copy-button" onClick={handleCopy}>{content?.devCenterCopy || "복사"}</button>
                        </div>
                        <div className="code-content">{`GET https://api.spatialcore.co.kr/v1/boundary?level=sido&code=11&apiKey=YOUR_API_KEY

Response:
{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {
        "name": "서울특별시",
        "code": "11"
      },
      "geometry": {
        "type": "Polygon",
        "coordinates": [[[126.5, 37.4], ...]]
      }
    }
  ]
}`}</div>
                    </div>
                    
                    <h2>{content?.devCenterStatistics || "지역별 통계 데이터"}</h2>
                    <p>{content?.devCenterStatisticsDesc || "각 행정구역별 인구, 면적 등 다양한 통계 데이터를 제공합니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// 지역별 통계 데이터 조회
const statistics = await api.getStatistics({
  level: 'sigungu',
  code: '1100',
  year: 2024
});

console.log(statistics);
// {
//   population: 550000,
//   area: 39.5, // km²
//   households: 240000,
//   density: 13924 // 명/km²
// }

// 다중 지역 통계 비교
const comparison = await api.compareStatistics({
  codes: ['1100', '2600', '4100'], // 강남구, 부산 해운대구, 수원시
  year: 2024
});`}</div>
                    </div>
                    
                    <h2>{content?.devCenterGeoJSON || "GeoJSON 형식 지원"}</h2>
                    <p>{content?.devCenterGeoJSONDesc || "표준 GeoJSON 형식으로 데이터를 제공하여 다양한 GIS 도구와 호환됩니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// GeoJSON 데이터를 지도에 표시
const geojsonData = await api.getBoundary({
  level: 'sido',
  format: 'geojson'
});

// Leaflet, Mapbox, OpenLayers 등과 호환
import L from 'leaflet';

L.geoJSON(geojsonData, {
  style: {
    color: '#3388ff',
    weight: 2,
    fillOpacity: 0.1
  }
}).addTo(map);

// GeoJSON 파일로 다운로드
api.downloadBoundary({
  level: 'sigungu',
  code: '1100',
  format: 'geojson',
  filename: 'gangnam.geojson'
});`}</div>
                    </div>
                </div>
            );
        }

        if (activeSection === 'routing-api') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterRoutingAPITitle || "길찾기 API"}</h1>
                    <p>{content?.devCenterRoutingAPIDesc || "최적의 경로를 탐색하고 실시간 교통정보를 반영하는 길찾기 API입니다."}</p>
                    
                    <h2>{content?.devCenterRouteTypes || "자동차/대중교통/도보 경로"}</h2>
                    <p>{content?.devCenterRouteTypesDesc || "다양한 이동 수단에 맞는 최적 경로를 제공합니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`import { RoutingAPI } from '@spatialcore/maps';

const routing = new RoutingAPI('YOUR_API_KEY');

// 자동차 경로 탐색
const carRoute = await routing.getRoute({
  origin: [126.9780, 37.5665], // 출발지
  destination: [127.0286, 37.4979], // 도착지
  mode: 'driving',
  avoidTolls: false,
  avoidHighways: false
});

console.log(carRoute);
// {
//   distance: 12500, // 미터
//   duration: 1800, // 초
//   polyline: [...],
//   steps: [...]
// }

// 대중교통 경로 탐색
const transitRoute = await routing.getRoute({
  origin: [126.9780, 37.5665],
  destination: [127.0286, 37.4979],
  mode: 'transit',
  departureTime: new Date('2024-01-15T09:00:00')
});

// 도보 경로 탐색
const walkingRoute = await routing.getRoute({
  origin: [126.9780, 37.5665],
  destination: [127.0286, 37.4979],
  mode: 'walking'
});`}</div>
                    </div>

                    <h2>{content?.devCenterTrafficInfo || "실시간 교통정보 반영"}</h2>
                    <p>{content?.devCenterTrafficInfoDesc || "실시간 교통 상황을 반영하여 가장 빠른 경로를 제안합니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// 실시간 교통정보 포함 경로
const routeWithTraffic = await routing.getRoute({
  origin: [126.9780, 37.5665],
  destination: [127.0286, 37.4979],
  mode: 'driving',
  trafficModel: 'best_guess', // 'pessimistic', 'optimistic'
  includeTraffic: true
});

console.log(routeWithTraffic);
// {
//   distance: 12500,
//   duration: 1800,
//   durationInTraffic: 2400, // 교통 정보 반영된 소요시간
//   trafficLevel: 'heavy', // 'light', 'moderate', 'heavy'
//   polyline: [...],
//   trafficInfo: [
//     {
//       segment: [...],
//       congestion: 0.7,
//       speed: 30 // km/h
//     }
//   ]
// }`}</div>
                    </div>
                    
                    <h2>{content?.devCenterWaypoints || "다중 경유지 지원"}</h2>
                    <p>{content?.devCenterWaypointsDesc || "여러 경유지를 거치는 경로도 계산할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// 여러 경유지를 거치는 경로
const routeWithWaypoints = await routing.getRoute({
  origin: [126.9780, 37.5665],
  destination: [127.0286, 37.4979],
  waypoints: [
    [127.0248, 37.5326], // 경유지 1
    [127.0280, 37.5133]  // 경유지 2
  ],
  optimizeWaypoints: true, // 경유지 순서 최적화
  mode: 'driving'
});

// 각 구간별 상세 정보
routeWithWaypoints.legs.forEach((leg, index) => {
  console.log('구간 ' + (index + 1) + ':', {
    distance: leg.distance,
    duration: leg.duration,
    startAddress: leg.startAddress,
    endAddress: leg.endAddress
  });
});`}</div>
                    </div>
                    
                    <h2>{content?.devCenterRouteDetails || "예상 소요시간/거리"}</h2>
                    <p>{content?.devCenterRouteDetailsDesc || "각 경로의 예상 소요시간과 거리 정보를 제공합니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// 경로 상세 정보
const route = await routing.getRoute({
  origin: [126.9780, 37.5665],
  destination: [127.0286, 37.4979],
  mode: 'driving'
});

console.log('총 거리:', route.distance, 'm');
console.log('총 소요시간:', Math.floor(route.duration / 60), '분');
console.log('예상 도착시간:', route.arrivalTime);

// 경로를 지도에 표시
map.addPolyline({
  path: route.polyline,
  strokeColor: '#FF0000',
  strokeWeight: 5
});

// 단계별 상세 정보
route.steps.forEach((step, index) => {
  console.log('단계 ' + (index + 1) + ':', {
    instruction: step.instruction, // "서울시청에서 우회전"
    distance: step.distance,
    duration: step.duration,
    polyline: step.polyline
  });
});`}</div>
                    </div>
                </div>
            );
        }

        if (activeSection === 'search-api') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterSearchAPITitle || "위치검색 API"}</h1>
                    <p>{content?.devCenterSearchAPIDesc || "주소 검색, 좌표 변환, POI 검색 등 다양한 위치 검색 기능을 제공합니다."}</p>
                    
                    <h2>{content?.devCenterGeocoding || "주소 검색 (지오코딩)"}</h2>
                    <p>{content?.devCenterGeocodingDesc || "주소를 입력하면 해당 위치의 좌표를 반환합니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`import { SearchAPI } from '@spatialcore/maps';

const search = new SearchAPI('YOUR_API_KEY');

// 주소 검색
const results = await search.geocode({
  query: '서울특별시 중구 세종대로 110',
  limit: 5
});

console.log(results);
// [
//   {
//     address: '서울특별시 중구 세종대로 110',
//     location: { lat: 37.5665, lng: 126.9780 },
//     type: 'road_address',
//     precision: 'high'
//   },
//   ...
// ]

// 가장 정확한 결과 사용
const bestMatch = results[0];
map.setCenter([bestMatch.location.lng, bestMatch.location.lat]);
map.addMarker({
  position: [bestMatch.location.lng, bestMatch.location.lat],
  title: bestMatch.address
});`}</div>
                    </div>

                    <h2>{content?.devCenterReverseGeocoding || "좌표 → 주소 변환 (역지오코딩)"}</h2>
                    <p>{content?.devCenterReverseGeocodingDesc || "좌표를 입력하면 해당 위치의 주소 정보를 반환합니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// 좌표로 주소 변환
const address = await search.reverseGeocode({
  lat: 37.5665,
  lng: 126.9780
});

console.log(address);
// {
//   roadAddress: '서울특별시 중구 세종대로 110',
//   jibunAddress: '서울특별시 중구 태평로1가 31',
//   buildingName: '서울시청',
//   sido: '서울특별시',
//   sigungu: '중구',
//   eupmyeondong: '태평로1가',
//   postcode: '04524'
// }

// 지도 클릭 시 주소 표시
map.on('click', async (event) => {
  const addressInfo = await search.reverseGeocode({
    lat: event.latlng[1],
    lng: event.latlng[0]
  });
  
  const marker = map.addMarker({
    position: event.latlng,
    title: addressInfo.roadAddress
  });
  
  marker.showInfoWindow({
    content: '도로명: ' + addressInfo.roadAddress + '<br>지번: ' + addressInfo.jibunAddress
  });
});`}</div>
                    </div>
                    
                    <h2>{content?.devCenterPOI || "POI(관심지점) 검색"}</h2>
                    <p>{content?.devCenterPOIDesc || "음식점, 주유소, 편의점 등 다양한 관심지점을 검색할 수 있습니다."}</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// POI 검색
const pois = await search.searchPOI({
  query: '스타벅스',
  location: [126.9780, 37.5665], // 중심 좌표
  radius: 2000, // 반경 (미터)
  category: 'restaurant', // 'restaurant', 'gas_station', 'convenience_store', etc.
  limit: 20
});

console.log(pois);
// [
//   {
//     name: '스타벅스 서울시청점',
//     location: { lat: 37.5665, lng: 126.9780 },
//     address: '서울특별시 중구 세종대로 110',
//     category: 'cafe',
//     phone: '02-1234-5678',
//     distance: 150 // 미터
//   },
//   ...
// ]

// 검색 결과를 지도에 표시
pois.forEach(poi => {
  map.addMarker({
    position: [poi.location.lng, poi.location.lat],
    title: poi.name,
    icon: 'poi-icon.png'
  });
});

// 카테고리별 필터링
const restaurants = await search.searchPOI({
  category: 'restaurant',
  location: [126.9780, 37.5665],
  radius: 1000
});`}</div>
                    </div>
                    
                    <h2>자동완성 기능</h2>
                    <p>검색어를 입력하면 실시간으로 자동완성 제안을 제공합니다.</p>
                    
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`// 자동완성 검색
const suggestions = await search.autocomplete({
  query: '서울',
  limit: 10
});

console.log(suggestions);
// [
//   { text: '서울특별시', type: 'sido' },
//   { text: '서울역', type: 'station' },
//   { text: '서울시청', type: 'building' },
//   ...
// ]

// 실시간 자동완성 구현
const searchInput = document.getElementById('search-input');

searchInput.addEventListener('input', async (e) => {
  const query = e.target.value;
  
  if (query.length > 1) {
    const suggestions = await search.autocomplete({
      query: query,
      limit: 5
    });
    
    // 자동완성 목록 표시
    displaySuggestions(suggestions);
  }
});

function displaySuggestions(suggestions) {
  const list = document.getElementById('suggestions-list');
  list.innerHTML = suggestions.map(item => 
    '<li onclick="selectSuggestion(\'' + item.text + '\')">' + item.text + '</li>'
  ).join('');
}

async function selectSuggestion(text) {
  const results = await search.geocode({ query: text });
  if (results.length > 0) {
    const location = results[0].location;
    map.setCenter([location.lng, location.lat]);
    map.addMarker({
      position: [location.lng, location.lat],
      title: text
    });
  }
}`}</div>
                    </div>
                </div>
            );
        }

        if (activeSection === 'getting-started') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterGettingStartedTitle || "시작하기 가이드"}</h1>
                    <p>{content?.devCenterGettingStartedDesc || "SpatialCore API를 빠르게 시작하는 방법을 알아보세요."}</p>
                    
                    <h2>1. {content?.devCenterAPIKeyTitle || "API 키 발급"}</h2>
                    <p>{content?.devCenterAPIKeyDesc || "API 키 발급은 문의하기를 통해 요청해 주세요. 담당자가 신속히 발급을 도와드립니다."}</p>
                    
                    <h2>2. {content?.devCenterSDKInstall || "SDK 설치"}</h2>
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>터미널</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">npm install @spatialcore/maps</div>
                    </div>
                    
                    <h2>3. {content?.devCenterMapInit || "지도 초기화"}</h2>
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`import { SpatialMap } from '@spatialcore/maps';

const map = new SpatialMap({
  apiKey: 'YOUR_API_KEY',
  container: 'map',
  center: [126.9780, 37.5665],
  zoom: 12
});`}</div>
                    </div>
                    
                    <h2>4. {content?.devCenterAddMarker || "마커 추가"}</h2>
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>JavaScript</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`map.addMarker({
  position: [126.9780, 37.5665],
  title: '서울시청'
});`}</div>
                    </div>

                    <h2>5. {content?.devCenterFullExample || "전체 예제"}</h2>
                    <p>{content?.devCenterFullExampleDesc || "다음은 완전한 HTML 페이지 예제입니다:"}</p>
                    <div className="code-block">
                        <div className="code-block-header">
                            <span>HTML</span>
                            <button className="copy-button" onClick={(e) => {
                                const code = e.target.closest('.code-block').querySelector('.code-content').textContent;
                                navigator.clipboard.writeText(code);
                                e.target.textContent = content?.devCenterCopied || '복사됨!';
                                setTimeout(() => { e.target.textContent = content?.devCenterCopy || '복사'; }, 2000);
                            }}>복사</button>
                        </div>
                        <div className="code-content">{`<!DOCTYPE html>
<html>
<head>
  <title>SpatialCore 지도 예제</title>
  <script src="https://cdn.spatialcore.co.kr/maps/v1/spatialcore-maps.js"></script>
</head>
<body>
  <div id="map" style="width: 100%; height: 500px;"></div>
  
  <script>
    const map = new SpatialMap({
      apiKey: 'YOUR_API_KEY',
      container: 'map',
      center: [126.9780, 37.5665],
      zoom: 12
    });

    // 마커 추가
    map.addMarker({
      position: [126.9780, 37.5665],
      title: '서울시청',
      clickable: true
    });

    // 마커 클릭 이벤트
    map.on('markerclick', (event) => {
      alert('마커가 클릭되었습니다: ' + event.marker.title);
    });

    // 지도 클릭 이벤트
    map.on('click', (event) => {
      console.log('클릭 위치:', event.latlng);
    });
  </script>
</body>
</html>`}</div>
                    </div>

                    <h2>{content?.devCenterNextSteps || "다음 단계"}</h2>
                    <ul>
                        <li><a href="#map-api" onClick={(e) => { e.preventDefault(); setActiveSection('map-api'); }}>{content?.devCenterMapAPI || "지도 API"}</a> {content?.devCenterMapAPIUse || "문서에서 더 자세한 기능을 확인하세요."}</li>
                        <li><a href="#spatial-api" onClick={(e) => { e.preventDefault(); setActiveSection('spatial-api'); }}>{content?.devCenterSpatialAPI || "공간정보 API"}</a> {content?.devCenterSpatialAPIUse || "로 행정구역 데이터를 활용하세요."}</li>
                        <li><a href="#routing-api" onClick={(e) => { e.preventDefault(); setActiveSection('routing-api'); }}>{content?.devCenterRoutingAPI || "길찾기 API"}</a> {content?.devCenterRoutingAPIUse || "로 경로 탐색 기능을 추가하세요."}</li>
                        <li><a href="#search-api" onClick={(e) => { e.preventDefault(); setActiveSection('search-api'); }}>{content?.devCenterSearchAPI || "위치검색 API"}</a> {content?.devCenterSearchAPIUse || "로 주소 검색 기능을 구현하세요."}</li>
                    </ul>

                    <h2>{content?.devCenterFAQ || "자주 묻는 질문"}</h2>
                    <div className="faq-section">
                        <h3>{content?.devCenterFAQKey || "API 키는 어디서 발급받나요?"}</h3>
                        <p>{content?.devCenterFAQKeyAnswer || "API 키는 문의하기를 통해 요청하시면 발급을 도와드립니다."}</p>

                        <h3>{content?.devCenterFAQBrowser || "어떤 브라우저를 지원하나요?"}</h3>
                        <p>{content?.devCenterFAQBrowserAnswer || "Chrome, Firefox, Safari, Edge 등 최신 브라우저를 모두 지원합니다. IE11은 제한적으로 지원됩니다."}</p>
                        
                        <h3>{content?.devCenterFAQMobile || "모바일 앱에서도 사용할 수 있나요?"}</h3>
                        <p>{content?.devCenterFAQMobileAnswer || "네, React Native, Flutter, 네이티브 iOS/Android 앱에서도 사용 가능한 SDK를 제공합니다."}</p>
                    </div>
                </div>
            );
        }

        if (activeSection === 'pricing') {
            return (
                <div className="developer-content">
                    <h1>{content?.devCenterPricingTitle || "가격 정책"}</h1>
                    <p>{content?.devCenterPricingDesc || "SpatialCore는 다양한 요구사항에 맞는 유연한 가격 정책을 제공합니다."}</p>
                    
                    <div className="pricing-contact">
                        <h2>{content?.devCenterPricingCustom || "맞춤형 가격 문의"}</h2>
                        <p>{content?.devCenterPricingContactDesc || "귀하의 프로젝트 규모와 요구사항에 맞는 최적의 플랜을 제안해드립니다."}</p>
                        <a href="#contact" className="cta-button primary">{content?.devCenterContactUs || "직접 문의하기"}</a>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="developer-content">
                <h1>{sidebarSections.flatMap(s => s.items).find(item => item.id === activeSection)?.label || '문서'}</h1>
                <p>이 섹션의 내용이 여기에 표시됩니다.</p>
            </div>
        );
    };

    return (
        <div className="developer-center">
            <div className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                ☰
            </div>
            
            <div className="developer-layout">
                {/* Left Sidebar */}
                <nav className={`developer-sidebar ${sidebarOpen ? 'open' : ''}`}>
                    {sidebarSections.map((section, index) => (
                        <div key={index} className="sidebar-section">
                            <h3 className="sidebar-section-title">{section.title}</h3>
                            <ul className="sidebar-nav">
                                {section.items.map(item => (
                                    <li key={item.id} className="sidebar-nav-item">
                                        <a
                                            href="#"
                                            className={`sidebar-nav-link ${activeSection === item.id ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setActiveSection(item.id);
                                                setSidebarOpen(false);
                                            }}
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Main Content */}
                <main className="developer-main">
                    <div ref={contentRef}>
                        {renderMainContent()}
                    </div>
                </main>

                {/* Right Sidebar - Table of Contents */}
                <aside className="developer-toc">
                    <h3 className="toc-title">On this page</h3>
                    {tocItems.length > 0 ? (
                        <ul className="toc-list">
                            {tocItems.map((item, index) => (
                                <li 
                                    key={item.id || index} 
                                    className={`toc-item ${item.level === 'h3' ? 'toc-item-h3' : ''}`}
                                >
                                    <a 
                                        href={`#${item.id}`}
                                        className="toc-link" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            scrollToHeading(item.id);
                                        }}
                                    >
                                        {item.text}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="toc-list">
                            <li className="toc-item">
                                <span className="toc-link" style={{ color: '#6c757d' }}>
                                    내용이 없습니다
                                </span>
                            </li>
                        </ul>
                    )}
                </aside>
            </div>
        </div>
    );
}

export default DeveloperCenter;